// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import { IDeviceListQuery, getDeviceList, IDeviceList, getDeviceById, updateDeviceQuota } from 'api/devices'
import { TablePaginationConfig } from 'antd/lib/table'
import { IDevice, DeviceType } from 'constants/device'
import { IGeneralSetting, ILastSetting, getDeviceSetting, getGeneralSetting, getLastReportSetting } from 'api/setting'
import { message } from 'antd'

export enum SearchType {
  CityID,
  DriverID,
  DeviceID
}

export type IDirectoryFilter = Omit<IDeviceListQuery, 'page' | 'pageSize' | 'cityID' | 'driverID' | 'bbox'>

export interface IDirectorySearch {
  value: number | string
  type: number
}

export interface IDirectoryState {
  filter: IDirectoryFilter
  search: IDirectorySearch
  list: IDeviceList
  page: {
    current: number
    total: number
    pageSize: number
  }
  loading: boolean
  detail: {
    device: IDevice
    setting: IGeneralSetting
    lastSetting: ILastSetting
  }
  detailLoading: boolean
}

export const directory = {
  state: {
    filter: {
      testMode: false,
      batteryLevel: [],
      firmwareVersion: [],
      taskType: [],
      deviceType: [],
      onlineStatus: [],
      status: [],
      dataUsageLevel: []
    },
    search: {
      value: null,
      type: SearchType.CityID
    },
    page: {
      current: 1,
      total: 0,
      pageSize: 20
    },
    deviceList: [],
    loading: false,
    detail: {
      device: null,
      setting: null,
      lastSetting: null
    },
    detailLoading: false
  },
  reducers: {
    setFilter (state: IDirectoryState, filter: Partial<IDirectoryFilter>) {
      return {
        ...state,
        filter: {
          ...state.filter,
          ...filter
        }
      }
    },
    setSearch (state: IDirectoryState, search: Partial<IDirectorySearch>) {
      return {
        ...state,
        search: {
          ...state.search,
          ...search
        }
      }
    },
    setPage (state: IDirectoryState, page: TablePaginationConfig) {
      return {
        ...state,
        page: {
          ...state.page,
          ...page
        }
      }
    },
    setDeviceList (state: IDirectoryState, data: IDeviceList) {
      return {
        ...state,
        deviceList: data
      }
    },
    setLoading (state: IDirectoryState, loading: boolean) {
      return {
        ...state,
        loading
      }
    },
    setDetailLoading (state: IDirectoryState, loading: boolean) {
      return {
        ...state,
        detailLoading: loading
      }
    },
    setDetail (
      state: IDirectoryState,
      payload: { device?: IDevice; setting?: IGeneralSetting; lastSetting?: ILastSetting }
    ) {
      return {
        ...state,
        detail: {
          ...state.detail,
          ...payload
        }
      }
    },
    clear () {
      return {
        filter: {
          testMode: false,
          batteryLevel: [],
          firmwareVersion: [],
          taskType: [],
          deviceType: [],
          onlineStatus: [],
          status: [],
          dataUsageLevel: []
        },
        search: {
          value: null,
          type: SearchType.CityID
        },
        page: {
          current: 1,
          total: 0,
          pageSize: 20
        },
        deviceList: [],
        loading: false,
        detail: {
          device: null,
          setting: null,
          lastSetting: null
        },
        detailLoading: false
      }
    }
  },
  effects: dispatch => ({
    async fetchDeviceList (query: IDeviceListQuery, state) {
      this.setLoading(true)
      if (query.testMode === false) {
        delete query.testMode
      }
      try {
        const res = await getDeviceList(query)
        this.setPage({
          current: res.metadata.currentPage,
          pageSize: res.metadata.pageSize,
          total: res.metadata.totalCount
        })
        this.setDeviceList(res.results)
      } catch (error) {
        console.log(error)
      } finally {
        this.setLoading(false)
      }
    },
    async loadPage (payload: { page: number; hasFilter: boolean }, state) {
      const pagination = state.directory.page
      this.setPage(payload)
      const latestPage: Partial<TablePaginationConfig> = { ...pagination, current: payload.page }
      let query: IDeviceListQuery = {
        page: latestPage.current,
        pageSize: latestPage.pageSize
      }
      if (payload.hasFilter) {
        const filter = state.directory.filter
        query = { ...query, ...filter }
      }

      const search = state.directory.search
      if (search.type === SearchType.CityID) {
        const city = state.city.cityList.find(d => d.name === search.value)
        query.cityID = city ? city.id : undefined
      } else if (search.type === SearchType.DriverID) {
        query.driverID = search.value
      } else {
        query.keyword = search.value
      }
      query.deviceType = [DeviceType.TwoW, DeviceType.FourW, DeviceType.Lite360Master]

      this.fetchDeviceList(query)
    },
    async searchDevices (payload: Partial<IDirectorySearch>, state) {
      let search
      if (payload) {
        this.setSearch(payload)
      }
      search = { ...state.directory.search, ...payload }
      const query: Partial<IDeviceListQuery> = { ...state.directory.filter };
      const page = state.directory.page
      query.page = 1
      query.pageSize = page.pageSize
      // const filter = {...state.directory.filter}
      // Object.keys(filter).forEach(key => {
      //   if (!(Array.isArray(filter[key]) && filter[key].length === 0)) {
      //     query[key] = filter[key]
      //   }
      // })
      if (search.type === SearchType.CityID) {
        const city = state.city.cityList.find(d => d.name === search.value)
        query.cityID = city ? city.id : undefined
      } else if (search.type === SearchType.DriverID) {
        query.driverID = search.value
      } else {
        query.keyword = search.value
      }
      this.fetchDeviceList(query)
    },
    async filterDevices (payload: Partial<IDirectoryFilter>, state) {
      let filter
      if (payload) {
        this.setFilter(payload)
      }
      filter = { ...state.directory.filter, ...payload }
      console.log('filter===', filter)
      const query: Partial<IDeviceListQuery> = { ...filter };
      // Object.keys(filter).forEach(key => {
      //   // deal with 360lite [master,slave]
      //   if(Array.isArray(filter[key])){
      //     filter[key] = filter[key].flat()
      //   }
      //   if (!(Array.isArray(filter[key]) && filter[key].length === 0)) {
      //     query[key] = filter[key]
      //   }
      // })

      const page = state.directory.page
      query.page = 1
      query.pageSize = page.pageSize

      const search = state.directory.search
      if (search.type === SearchType.CityID) {
        const city = state.city.cityList.find(d => d.name === search.value)
        query.cityID = city ? city.id : undefined
      } else if (search.type === SearchType.DriverID) {
        query.driverID = search.value
      } else {
        query.keyword = search.value
      }
      
      this.fetchDeviceList(query)
    },
    async fetchDetail (deviceID: string, state) {
      this.setDetailLoading(true)
      let deviceRes: IDevice = null
      let lastSetting: ILastSetting = null
      let settingRes = {
        imageryResolution: null,
        videoResolution: null,
        uploadMethod: null,
        intervalType: null,
        captureInterval: null,
        captureDistanceInterval: null,
        powerSave: null,
        aiImageQuality: null,
        aiTrafficSign: null,
        aiPoi: null,
        aiPrivacy: null,
        gpsUploadInterval: null,
        imuUploadInterval: null,
        workTimeStart: null,
        workTimeEnd: null,
        trafficQuota: null,
        alertThreshold: null, // %
        hardThreshold: null, // %
        uploadFmt: null,
        imageryFmt: null
      }

      try {
        deviceRes = await getDeviceById(deviceID)
        lastSetting = await getLastReportSetting(deviceID)
      } catch (error) {
        throw error
      }

      (deviceRes && deviceRes.testMode ? getDeviceSetting(deviceRes.deviceID) : getGeneralSetting(deviceRes.cityID))
      .then(deviceSettingRes => {
        settingRes = deviceSettingRes
      })
      .catch(error => {
        console.log('error', error)
      })
      .finally(() => {
        this.setDetailLoading(false)
        this.setDetail({ device: deviceRes, setting: settingRes, lastSetting: lastSetting })
      })

      // try {
      //   settingRes = await getDeviceSetting(deviceID)
      // } catch (error) {
      //   if (error.response.status === 404) {
      //     settingRes = await getGeneralSetting(deviceRes.cityID)
      //   }
      // } finally {
      //   this.setDetailLoading(false)
      // }
    },
    async updateQuota (deviceID) {
      try {
        await updateDeviceQuota(deviceID)
        this.fetchDetail(deviceID)
        message.success('reset successfully')
      } catch (error) {
        message.info(`Reset Failed ${error}`)
      }
    }
  })
}
